import { IRiskOption } from "src/core/constants/options";
import { CustomRiskLevel } from "src/core/constants/risk";
import {
  AdvancedCorporationSearchQuery,
  ElementInputValue,
  FolderFileQuery,
  Mission,
  SearchCustomerFolderByStringQuery,
  SearchPersonByFirstNameAndLastNameAndBirthdateQuery,
} from "src/core/gql/graphql";
import { IMission } from "src/core/hooks/folder/useFolderMissions";
import { ICustomDuplicateFolder, ICustomDuplicatePerson } from "src/core/interfaces/Folders";
import { getInputValue } from "src/core/utils/folderAnalysis";
import { isMissionActive } from "src/core/utils/folders";

/**
 * @description Map le résultat de la recherche avancée des sociétés.
 * @returns Tableau d'objets @see{@link ICustomDuplicateFolder}
 */
export const mapAdvancedCorporationSearchToDuplicateFolders = (
  data?: AdvancedCorporationSearchQuery
): ICustomDuplicateFolder[] => {
  if (!data) return [];

  return data.advancedCorporationSearch.corporations.map(
    (
      corporation: AdvancedCorporationSearchQuery["advancedCorporationSearch"]["corporations"][number]
    ) => {
      const id = corporation.customerFolders[0]?.folderFiles?.summaries?.[0]?.id ?? "";
      const raisonSociale = corporation.name?.stringValue ?? "";
      const siren = corporation.siren?.stringValue ?? "";
      const isCustomer = corporation.isCustomer ?? false;

      return { id, raisonSociale, siren, isCustomer };
    }
  );
};

/**
 * @description Map le résultat de la recherche avancée des sociétés.
 * @returns Tableau d'objets @see{@link ICustomDuplicateFolder}
 */
export const mapAdvancedPersonSearchToDuplicatePersons = (
  data?: SearchPersonByFirstNameAndLastNameAndBirthdateQuery
): ICustomDuplicatePerson[] => {
  if (!data) return [];

  return data.advancedPersonSearch.persons.map((person) => {
    const id = person.customerFolders[0]?.folderFiles?.summaries?.[0]?.id ?? "";
    const lastName = person.lastName?.stringValue ?? "";
    const firstName = person.firstName?.stringValue ?? "";
    const birthDate = person.birthDate?.stringValue ?? "";
    const isCustomer = (person as { isCustomer?: boolean }).isCustomer ?? false;

    return { id, lastName, firstName, birthDate, isCustomer };
  });
};

/**
 * @description Map le résultat de la recherche avancée des sociétés.
 * @returns Tableau d'objets @see{@link ICustomDuplicateFolder}
 */
export const mapAdvancedCorporationAndPersonSearchToDuplicateFolders = (
  data?: AdvancedCorporationSearchQuery
): ICustomDuplicateFolder[] => {
  if (!data) return [];
  return data.advancedCorporationSearch.corporations.map(
    (
      corporation: AdvancedCorporationSearchQuery["advancedCorporationSearch"]["corporations"][number]
    ) => {
      const id = corporation.customerFolders[0]?.folderFiles?.summaries?.[0]?.id ?? "";
      const raisonSociale = corporation.name?.stringValue ?? "";
      const siren = corporation.siren?.stringValue ?? "";
      const isCustomer = corporation.isCustomer ?? false;

      return { id, raisonSociale, siren, isCustomer };
    }
  );
};

/**
 * @description Map le résultat de la recherche sur la raison sociale.
 * @returns Tableau d'objets @see{@link ICustomDuplicateFolder}
 */
export const mapFolderFilesToDuplicateFolders = (
  data?: SearchCustomerFolderByStringQuery
): ICustomDuplicateFolder[] => {
  if (!data) return [];

  return data.customerFoldersPage.summaries.map(
    (
      summary: NonNullable<SearchCustomerFolderByStringQuery["customerFoldersPage"]>["summaries"][0]
    ) => {
      const id = summary.customerFolder?.folderFiles?.summaries?.[0]?.id ?? "";
      const raisonSociale =
        summary.customer?.inputValues?.find((input) => input.inputField?.fieldCode === "name")
          ?.stringValue ?? "";
      const siren =
        summary.customer?.inputValues?.find(
          (input) => input.inputField?.fieldCode === "registration"
        )?.stringValue ?? "";

      return { id, raisonSociale, siren };
    }
  );
};

export const getFolderFieldValue = (
  folderFile: FolderFileQuery,
  fieldCode: string,
  defaultValue = ""
) => {
  return (
    folderFile?.folderFile?.fileCustomerFolder?.customer?.allInputValues.find(
      (value) => value.inputField.fieldCode === fieldCode
    )?.stringValue ?? defaultValue
  );
};

export const mapMissionToIMission = (
  mission: Mission,
  missionTypeOptions: IRiskOption[]
): IMission => {
  const missionType = missionTypeOptions?.find(
    (option) =>
      option.value === getInputValue(mission.inputValues as ElementInputValue[], "missionTypeCode")
  );

  return {
    id: mission.id,
    missionType: missionType?.label || "",
    missionTypeCode: missionType?.value || "",
    renewDate: getInputValue(mission.inputValues as ElementInputValue[], "renewDate"),
    isClosable: mission.isClosable,
    isEditable: mission.isEditable,
    isActive: isMissionActive(mission),
    risk: missionType?.risk || CustomRiskLevel.UNDEFINED,
    status: mission.status,
    statusComment: mission?.statusComment ?? "",
    lastModification: mission?.adminStatus?.lastModification ?? null,
    creation: mission?.adminStatus?.creation ?? null,
  };
};
